import React from "react";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { TypedObject } from "@portabletext/types";

const defaultComponents: PortableTextComponents = {
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 my-4 pl-4">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 my-4 pl-4">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
  },
  block: {
    h1: ({ children }) => (
      <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-semibold mt-6 mb-3">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-semibold mt-5 mb-2">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg md:text-xl font-semibold mt-4 mb-1">{children}</h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-prime-300 pl-4 italic my-6 text-gray-700">
        {children}
      </blockquote>
    ),
    normal: ({ children }) => (
      <p className="leading-relaxed my-4">{children}</p>
    ),
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    underline: ({ children }) => <span className="underline">{children}</span>,
    "strike-through": ({ children }) => <del>{children}</del>,
    link: ({ children, value }) => {
      const rel = !value.href?.startsWith("/")
        ? "noreferrer noopener"
        : undefined;
      const target = !value.href?.startsWith("/") ? "_blank" : undefined;
      return (
        <a
          href={value.href}
          rel={rel}
          target={target}
          className="text-prime-600 hover:underline"
        >
          {children}
        </a>
      );
    },
  },
  types: {
    image: ({ value }: any) => {
      if (!value?.url) {
        // If the query didn't project 'url' (e.g. asset->url), we might need to use urlFor.
        // But based on the project conventions, the query should project it.
        console.warn(
          'RichText Image block missing URL. Ensure your query projects "url": asset->url'
        );
        return (
          <div className="my-8 flex justify-center items-center bg-gray-200 aspect-video rounded-lg">
            <span className="text-gray-500 text-sm">Bild nicht verfügbar</span>
          </div>
        );
      }
      return (
        <figure className="my-8">
          <img
            src={value.url}
            alt={value.alt || "Image"}
            loading="lazy"
            className="max-w-full h-auto mx-auto rounded-lg shadow-md"
          />
          {value.caption && (
            <figcaption className="text-center text-sm text-gray-600 mt-3 italic">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

interface RichTextProps {
  value: TypedObject | TypedObject[];
  components?: PortableTextComponents;
  className?: string;
}

export const RichText: React.FC<RichTextProps> = ({
  value,
  components,
  className,
}) => {
  if (!value) return null;

  return (
    <div className={`prose prose-lg max-w-none ${className || ""}`}>
      <PortableText
        value={value}
        components={{ ...defaultComponents, ...components }}
      />
    </div>
  );
};
